import { Check, Circle, Loader2, Minus, X } from "lucide-react";

import { cn } from "@/lib/utils";
import type { KaiPlanArtifact, KaiPlanStep, KaiPlanStepStatus } from "./kaiTypes";
import { isSynthesisPlanStep } from "./kaiPlanHelpers";

function statusLabel(status: KaiPlanStepStatus, step: KaiPlanStep): string {
  switch (status) {
    case "ok":
      return "Done";
    case "error":
      return "Failed";
    case "running":
      return isSynthesisPlanStep(step) ? "Summarizing…" : "Running";
    case "waiting":
      return "Waiting for your input";
    case "skipped":
      return "Skipped";
    case "cancelled":
      return "Cancelled";
    default:
      return "Pending";
  }
}

function StepIcon({ status }: { status: KaiPlanStepStatus }) {
  const className = "size-3.5 shrink-0";
  switch (status) {
    case "ok":
      return <Check className={cn(className, "text-emerald-500")} />;
    case "error":
      return <X className={cn(className, "text-destructive")} />;
    case "running":
    case "waiting":
      return <Loader2 className={cn(className, "animate-spin text-primary")} />;
    case "skipped":
    case "cancelled":
      return <Minus className={cn(className, "text-muted-foreground")} />;
    default:
      return <Circle className={cn(className, "text-muted-foreground/50")} />;
  }
}

/** Live or persisted plan-and-execute step list. */
export function KaiPlanProgress({ spec }: { spec: KaiPlanArtifact }) {
  return (
    <div className="glass-panel w-full max-w-[min(100%,38rem)] rounded-2xl px-4 py-3">
      <p className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        {spec.title ?? "Plan"}
      </p>
      <ol className="space-y-1.5">
        {spec.steps.map((step) => {
          const status = step.status ?? "pending";
          return (
            <li key={step.id} className="flex items-start gap-2 text-sm">
              <span className="mt-0.5">
                <StepIcon status={status} />
              </span>
              <span className="min-w-0 flex-1 leading-snug">
                <span className="text-foreground">{step.label}</span>
                <span className="ml-1.5 text-xs text-muted-foreground">
                  {statusLabel(status, step)}
                </span>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
